/**
 * Cron Registry (B6) — catálogo estático + overrides en `sm_cron_registry`.
 * El toggle `enabled` evita ejecutar el job sin redeploy (gate en runCronJob).
 * `schedule_active=false` documenta crons Netlify con schedule comentado (p.ej. analytics-ttl).
 */
import { sql } from "@/lib/db";

export type CronDomain = "orders" | "billing" | "observability" | "marketing" | "inventory" | "infra" | "ai";

export const CRON_DOMAIN_LABELS: Record<CronDomain, string> = {
  orders: "Órdenes",
  billing: "Billing",
  observability: "Observabilidad",
  marketing: "Marketing",
  inventory: "Inventario",
  infra: "Infraestructura",
  ai: "IA",
};

export type CronRegistryCatalogEntry = {
  jobId: string;
  label: string;
  description: string;
  endpointPath: string;
  netlifyFn: string;
  /** Expresión cron UTC o null si no hay schedule. */
  scheduleHint: string | null;
  /** false = Netlify schedule comentado / OFF en código. */
  scheduleActive: boolean;
  defaultEnabled: boolean;
  /** Agrupación funcional para la UI del registry (SCRUM-312). */
  domain: CronDomain;
};

export const CRON_REGISTRY_CATALOG: readonly CronRegistryCatalogEntry[] = [
  {
    jobId: "order-cleanup",
    label: "Order cleanup",
    description: "Limpieza de órdenes huérfanas / estados viejos",
    endpointPath: "/api/jobs/order-cleanup",
    netlifyFn: "scheduled-order-cleanup",
    scheduleHint: "0 6 * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "orders",
  },
  {
    jobId: "trial-expiry",
    label: "Trial expiry",
    description: "Expira free_trial vencidos",
    endpointPath: "/api/jobs/trial-expiry",
    netlifyFn: "scheduled-trial-expiry",
    scheduleHint: "0 12 * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "billing",
  },
  {
    jobId: "subscription-billing",
    label: "Subscription billing",
    description: "Cargos recurrentes / dunning",
    endpointPath: "/api/jobs/subscription-billing",
    netlifyFn: "scheduled-subscription-billing",
    scheduleHint: "0 11 * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "billing",
  },
  {
    jobId: "observability-purge",
    label: "Observability purge",
    description: "Purge audit/errors + techo business_events",
    endpointPath: "/api/jobs/observability-purge",
    netlifyFn: "scheduled-observability-purge",
    scheduleHint: "0 3 * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "observability",
  },
  {
    jobId: "analytics-ttl-purge",
    label: "Analytics TTL purge",
    description: "TTL sm_business_events por plan (B5)",
    endpointPath: "/api/jobs/analytics-ttl-purge",
    netlifyFn: "scheduled-analytics-ttl-purge",
    scheduleHint: "0 4 * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "observability",
  },
  {
    jobId: "digital-lifecycle",
    label: "Digital lifecycle",
    description: "Ciclo de vida productos digitales",
    endpointPath: "/api/jobs/digital-lifecycle",
    netlifyFn: "scheduled-digital-lifecycle",
    scheduleHint: null,
    scheduleActive: false,
    defaultEnabled: false,
    domain: "inventory",
  },
  {
    jobId: "shipnow-sync-status",
    label: "Shipnow sync",
    description: "Sync estados Shipnow",
    endpointPath: "/api/jobs/shipnow-sync-status",
    netlifyFn: "scheduled-shipnow-sync-status",
    scheduleHint: "0 * * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "orders",
  },
  {
    jobId: "abandoned-cart",
    label: "Abandoned cart",
    description: "Recovery emails checkout abandonado",
    endpointPath: "/api/cron/abandoned-cart",
    netlifyFn: "scheduled-abandoned-cart",
    scheduleHint: "0 * * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "marketing",
  },
  {
    jobId: "reengagement",
    label: "Reengagement",
    description: "Emails de reenganche",
    endpointPath: "/api/cron/reengagement",
    netlifyFn: "scheduled-reengagement",
    scheduleHint: "0 9 * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "marketing",
  },
  {
    jobId: "onboarding-nudge",
    label: "Onboarding nudge",
    description: "Nudge onboarding merchant",
    endpointPath: "/api/cron/onboarding-nudge",
    netlifyFn: "scheduled-onboarding-nudge",
    scheduleHint: "0 10 * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "marketing",
  },
  {
    jobId: "repurchase-top-sku",
    label: "Repurchase top SKU",
    description: "Sugerencia recompra top SKU",
    endpointPath: "/api/cron/repurchase-top-sku",
    netlifyFn: "scheduled-repurchase-top-sku",
    scheduleHint: "0 14 * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "marketing",
  },
  {
    jobId: "reservation-reminders",
    label: "Reservation reminders",
    description: "Recordatorios de reserva",
    endpointPath: "/api/cron/reservation-reminders",
    netlifyFn: "scheduled-reservation-reminders",
    scheduleHint: "0 12 * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "orders",
  },
  {
    jobId: "stock-reservation-cleanup",
    label: "Stock reservation cleanup",
    description: "Libera holds de sm_stock_reservations con TTL expirado",
    endpointPath: "/api/jobs/stock-reservation-cleanup",
    netlifyFn: "scheduled-stock-reservation-cleanup",
    scheduleHint: "0 * * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "inventory",
  },
  {
    jobId: "stock-alerts",
    label: "Alertas de stock",
    description: "Low stock / batch expiry / low margin para tenants con supply_chain",
    endpointPath: "/api/cron/stock-alerts",
    netlifyFn: "scheduled-stock-alerts",
    scheduleHint: "0 10 * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "inventory",
  },
  {
    jobId: "provider-refresh",
    label: "Refresh providers",
    description: "Refresca snapshots de health de providers de plataforma",
    endpointPath: "/api/cron/provider-refresh",
    netlifyFn: "scheduled-provider-refresh",
    scheduleHint: "*/20 * * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "infra",
  },
  // FASE 9 — Renata Specialized Agents
  {
    jobId: "renata-agents",
    label: "Renata Background Agents",
    description: "Ejecuta agentes especializados de Renata en background: genera insights y sugerencias para todos los tenants con ai_assistant habilitado.",
    endpointPath: "/api/cron/renata-agents",
    netlifyFn: "scheduled-renata-agents",
    scheduleHint: "0 8 * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "ai",
  },
  {
    jobId: "custom-domain-reprobe",
    label: "Re-probe custom domain",
    description: "Re-chequea DNS de tenants con dominio propio. Probe on-demand ya existe en Settings → Publicar.",
    endpointPath: "/api/cron/custom-domain-reprobe",
    netlifyFn: "scheduled-custom-domain-reprobe",
    scheduleHint: "0 9 * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "infra",
  },
  // SERV-GAP-12 (docs-system/GAPS.md): estos dos jobs de servicios (SCRUM-260)
  // ya existían como Netlify functions reales con schedule comentado, pero
  // nunca se agregaron a este catálogo — quedaban fuera del panel de control
  // /admin → Tareas programadas.
  {
    jobId: "service-booking-reminders",
    label: "Recordatorios de turno",
    description: "Recordatorio día anterior para turnos de servicio confirmados (nicho servicios)",
    endpointPath: "/api/cron/service-booking-reminders",
    netlifyFn: "scheduled-service-booking-reminders",
    scheduleHint: "0 12 * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "orders",
  },
  {
    jobId: "service-review-requests",
    label: "Pedidos de reseña post-turno",
    description: "Solicita reseña al cliente tras el fin de un turno de servicio (nicho servicios)",
    endpointPath: "/api/cron/service-review-requests",
    netlifyFn: "scheduled-service-review-requests",
    scheduleHint: "0 * * * *",
    scheduleActive: false,
    defaultEnabled: false,
    domain: "orders",
  },
] as const;

export type CronRegistryRow = {
  jobId: string;
  enabled: boolean;
  scheduleHint: string | null;
  endpointPath: string;
  label: string;
  description: string;
  netlifyFn: string | null;
  scheduleActive: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  source: "db" | "catalog";
  /** Del catálogo estático — sm_cron_registry no persiste domain. */
  domain: CronDomain;
};

function mapDbRow(r: Record<string, unknown>): CronRegistryRow {
  const jobId = String(r.job_id);
  return {
    jobId,
    enabled: Boolean(r.enabled),
    scheduleHint: r.schedule_hint != null ? String(r.schedule_hint) : null,
    endpointPath: String(r.endpoint_path ?? ""),
    label: String(r.label ?? ""),
    description: String(r.description ?? ""),
    netlifyFn: r.netlify_fn != null ? String(r.netlify_fn) : null,
    scheduleActive: r.schedule_active !== false,
    updatedAt: r.updated_at ? String(r.updated_at) : null,
    updatedBy: r.updated_by != null ? String(r.updated_by) : null,
    source: "db",
    domain: cronRegistryCatalogEntry(jobId)?.domain ?? "infra",
  };
}

/** Upsert defaults del catálogo (idempotente). */
export async function ensureCronRegistrySeeded(): Promise<void> {
  const db = sql();
  for (const c of CRON_REGISTRY_CATALOG) {
    await db`
      INSERT INTO sm_cron_registry (
        job_id, enabled, schedule_hint, endpoint_path, label, description,
        netlify_fn, schedule_active, updated_at
      ) VALUES (
        ${c.jobId}, ${c.defaultEnabled}, ${c.scheduleHint}, ${c.endpointPath},
        ${c.label}, ${c.description}, ${c.netlifyFn}, ${c.scheduleActive}, NOW()
      )
      ON CONFLICT (job_id) DO UPDATE SET
        schedule_hint = EXCLUDED.schedule_hint,
        endpoint_path = EXCLUDED.endpoint_path,
        label = EXCLUDED.label,
        description = EXCLUDED.description,
        netlify_fn = EXCLUDED.netlify_fn,
        schedule_active = EXCLUDED.schedule_active,
        enabled = CASE
          WHEN sm_cron_registry.updated_by IS NULL THEN EXCLUDED.enabled
          ELSE sm_cron_registry.enabled
        END
    `;
  }
}

export async function listCronRegistry(): Promise<CronRegistryRow[]> {
  await ensureCronRegistrySeeded();
  const db = sql();
  const rows = await db`SELECT * FROM sm_cron_registry ORDER BY job_id ASC`;
  const byId = new Map(rows.map((r) => [String((r as { job_id: string }).job_id), mapDbRow(r as Record<string, unknown>)]));

  // Garantizar que el catálogo completo aparece aunque falte fila
  const out: CronRegistryRow[] = [];
  for (const c of CRON_REGISTRY_CATALOG) {
    const existing = byId.get(c.jobId);
    if (existing) {
      out.push(existing);
      byId.delete(c.jobId);
    } else {
      out.push({
        jobId: c.jobId,
        enabled: c.defaultEnabled,
        scheduleHint: c.scheduleHint,
        endpointPath: c.endpointPath,
        label: c.label,
        description: c.description,
        netlifyFn: c.netlifyFn,
        scheduleActive: c.scheduleActive,
        updatedAt: null,
        updatedBy: null,
        source: "catalog",
        domain: c.domain,
      });
    }
  }
  // extras en DB no catalogados
  for (const extra of byId.values()) out.push(extra);
  return out;
}

export async function setCronRegistryEnabled(
  jobId: string,
  enabled: boolean,
  updatedBy?: string | null,
): Promise<CronRegistryRow | null> {
  await ensureCronRegistrySeeded();
  const catalog = CRON_REGISTRY_CATALOG.find((c) => c.jobId === jobId);
  if (!catalog) return null;
  const db = sql();
  const rows = await db`
    INSERT INTO sm_cron_registry (
      job_id, enabled, schedule_hint, endpoint_path, label, description,
      netlify_fn, schedule_active, updated_at, updated_by
    ) VALUES (
      ${catalog.jobId}, ${enabled}, ${catalog.scheduleHint}, ${catalog.endpointPath},
      ${catalog.label}, ${catalog.description}, ${catalog.netlifyFn}, ${catalog.scheduleActive},
      NOW(), ${updatedBy ?? null}
    )
    ON CONFLICT (job_id) DO UPDATE SET
      enabled = EXCLUDED.enabled,
      updated_at = NOW(),
      updated_by = EXCLUDED.updated_by
    RETURNING *
  `;
  return rows[0] ? mapDbRow(rows[0] as Record<string, unknown>) : null;
}

/** true si el job puede correr (default true si no hay fila / tabla ausente). */
export async function isCronRegistryEnabled(jobId: string): Promise<boolean> {
  try {
    const db = sql();
    const rows = await db`
      SELECT enabled FROM sm_cron_registry WHERE job_id = ${jobId} LIMIT 1
    `;
    if (!rows[0]) {
      const c = CRON_REGISTRY_CATALOG.find((x) => x.jobId === jobId);
      return c ? c.defaultEnabled : false;
    }
    return Boolean((rows[0] as { enabled: boolean }).enabled);
  } catch {
    // tabla aún no migrada → fail-closed (OFF) durante hardening
    const c = CRON_REGISTRY_CATALOG.find((x) => x.jobId === jobId);
    return c ? c.defaultEnabled : false;
  }
}

export function cronRegistryCatalogEntry(jobId: string): CronRegistryCatalogEntry | undefined {
  return CRON_REGISTRY_CATALOG.find((c) => c.jobId === jobId);
}

/** Valida expresión cron de 5 campos (min hour dom mon dow). */
export function validateCronExpression(expr: string): { ok: true } | { ok: false; error: string } {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return { ok: false, error: "Se esperan 5 campos: min hour dom mon dow" };
  const fieldRe = /^(\*|\*\/\d+|\d+(?:-\d+)?(?:,\d+(?:-\d+)?)*)$/;
  for (const p of parts) {
    if (!fieldRe.test(p)) return { ok: false, error: `Campo inválido: ${p}` };
  }
  return { ok: true };
}

function cronFieldMatches(field: string, value: number): boolean {
  if (field === "*") return true;
  if (field.startsWith("*/")) {
    const step = Number(field.slice(2));
    return Number.isFinite(step) && step > 0 && value % step === 0;
  }
  for (const part of field.split(",")) {
    if (part.includes("-")) {
      const [a, b] = part.split("-").map(Number);
      if (value >= a && value <= b) return true;
    } else if (Number(part) === value) return true;
  }
  return false;
}

/** Próxima ejecución UTC a partir de `from` (best-effort, sin seconds). */
export function previewNextCronRun(expr: string, from: Date = new Date()): Date | null {
  const v = validateCronExpression(expr);
  if (!v.ok) return null;
  const [minF, hourF, domF, monF, dowF] = expr.trim().split(/\s+/);
  const cursor = new Date(from.getTime());
  cursor.setUTCSeconds(0, 0);
  cursor.setUTCMinutes(cursor.getUTCMinutes() + 1);
  for (let i = 0; i < 366 * 24 * 60; i++) {
    const m = cursor.getUTCMinutes();
    const h = cursor.getUTCHours();
    const dom = cursor.getUTCDate();
    const mon = cursor.getUTCMonth() + 1;
    const dow = cursor.getUTCDay();
    if (
      cronFieldMatches(minF, m)
      && cronFieldMatches(hourF, h)
      && cronFieldMatches(domF, dom)
      && cronFieldMatches(monF, mon)
      && cronFieldMatches(dowF, dow)
    ) {
      return new Date(cursor.getTime());
    }
    cursor.setUTCMinutes(cursor.getUTCMinutes() + 1);
  }
  return null;
}

export async function setCronRegistrySchedule(
  jobId: string,
  scheduleHint: string | null,
  updatedBy?: string | null,
): Promise<CronRegistryRow | null> {
  await ensureCronRegistrySeeded();
  const catalog = CRON_REGISTRY_CATALOG.find((c) => c.jobId === jobId);
  if (!catalog) return null;
  if (scheduleHint != null && scheduleHint.trim()) {
    const v = validateCronExpression(scheduleHint);
    if (!v.ok) throw new Error(v.error);
  }
  const db = sql();
  const hint = scheduleHint != null && scheduleHint.trim() ? scheduleHint.trim() : null;
  const rows = await db`
    INSERT INTO sm_cron_registry (
      job_id, enabled, schedule_hint, endpoint_path, label, description,
      netlify_fn, schedule_active, updated_at, updated_by
    ) VALUES (
      ${catalog.jobId}, ${catalog.defaultEnabled}, ${hint}, ${catalog.endpointPath},
      ${catalog.label}, ${catalog.description}, ${catalog.netlifyFn}, ${catalog.scheduleActive},
      NOW(), ${updatedBy ?? null}
    )
    ON CONFLICT (job_id) DO UPDATE SET
      schedule_hint = EXCLUDED.schedule_hint,
      updated_at = NOW(),
      updated_by = EXCLUDED.updated_by
    RETURNING *
  `;
  return rows[0] ? mapDbRow(rows[0] as Record<string, unknown>) : null;
}
